import { useFormikContext } from 'formik';
import { useEffect } from 'react';

import { semverCompare } from '@/react/common/semver-utils';
import { useSystemStatus } from '@/react/portainer/system/useSystemStatus';

import { TextTip } from '@@/Tip/TextTip';

import { FormValues } from './types';
import { ScheduledTimeField } from './ScheduledTimeField';
import { Info } from './useInfo';

/**
 * in-case agents don't have any version field, it means they are version less then 2.15.x or that they still not associated.
 */
const DEFAULT_MIN_VERSION = '2.14.10';

export function UpdateScheduleDetailsFieldset({
  info,
  hasGroupSelected,
}: {
  info: Info;
  hasGroupSelected: boolean;
}) {
  const {
    values: { version, agentImage, updaterImage, registryId },
    setFieldValue,
  } = useFormikContext<FormValues>();

  const statusQuery = useSystemStatus();

  useEffect(() => {
    const latestVersion = statusQuery?.data?.Version;
    if (!latestVersion || version === latestVersion) {
      return;
    }

    if (registryId === 0 && agentImage === `portainer/agent:${version}`) {
      setFieldValue('agentImage', `portainer/agent:${latestVersion}`);
    }

    if (
      registryId === 0 &&
      updaterImage === `portainer/portainer-updater:${version}`
    ) {
      setFieldValue(
        'updaterImage',
        `portainer/portainer-updater:${latestVersion}`
      );
    }

    setFieldValue('version', latestVersion);
  }, [
    setFieldValue,
    statusQuery?.data?.Version,
    version,
    agentImage,
    updaterImage,
    registryId,
  ]);

  return (
    <>
      <EnvironmentInfo info={info} version={version} />

      {info.HasLocalTimeZone && hasGroupSelected && <ScheduledTimeField />}
      {info.HasNoLocalTimeZone && (
        <TextTip inline={false}>
          These edge groups have older versions of the edge agent that do not
          support scheduling, these will happen immediately
        </TextTip>
      )}
    </>
  );
}

function EnvironmentInfo({ info, version }: { info: Info; version: string }) {
  if (!info.OutdatedCount && !info.UpToDateCount) {
    return (
      <TextTip color="orange">
        No environments options for the selected edge groups
      </TextTip>
    );
  }

  if (!version) {
    return null;
  }

  if (semverCompare(version, info.MinAgentVersion || DEFAULT_MIN_VERSION) < 0) {
    return <TextTip>No supported versions available</TextTip>;
  }

  if (info.OutdatedCount === 0) {
    return (
      <TextTip color="orange">
        All edge agents are already running version {version}
      </TextTip>
    );
  }

  return (
    <TextTip color="blue">
      {info.UpToDateCount > 0 && (
        <>
          {info.UpToDateCount} edge agent
          {info.UpToDateCount > 1 ? 's are' : ' is'} currently running version
          greater than or equal to {version}, and{' '}
        </>
      )}
      {info.OutdatedCount > 0 && (
        <>
          {info.OutdatedCount} will be updated to version {version}
        </>
      )}
    </TextTip>
  );
}
